import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Button,
  Spinner,
  Text,
  makeStyles,
  Tooltip,
  ProgressBar,
} from "@fluentui/react-components";
import { toast } from 'react-hot-toast';
import { Dismiss24Regular } from "@fluentui/react-icons";
import * as pdfjsLib from 'pdfjs-dist';

// The worker to match the installed version (3.11.174)
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

import JSZip from 'jszip';


/**
 * Defines the Fluent UI styles for the UploadModal component.
 * Utilizes CSS-in-JS to create scoped class names for the dropzone, overlays,
 * file lists, progress bars, and modal structure.
 * 
 * @returns {Object} An object containing the generated CSS class names.
 */
const useStyles = makeStyles({
  dialogSurface: {
    padding: "10px",
    width: "90vw",
    maxWidth: "500px",
    paddingTop: "5px",
    paddingBottom: "20px",
  },
  dialogBody: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    overflowY: "auto",
    overflowX: "hidden",
  },
  dialogTitle: {
    fontSize: "14px"
  },
  dialogActions: {
    display: "flex",
    flexDirection: "row !important"
  },
  fileInputContainer: {
    position: "relative",
    padding: "10px 10px",
    minHeight: "110px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    boxSizing: "border-box",
    border: "2px dashed #ccc",
    borderRadius: "8px",
    textAlign: "center",
    cursor: "pointer",
    flexShrink: 0,
    "&:hover": {
      borderColor: "#999",
      "& span:first-of-type": {
        fontWeight: "500",
        textDecoration: "underline",
      },
    },
    "&[aria-disabled='true']": {
      cursor: "not-allowed",
      backgroundColor: "#f5f5f5",
      borderColor: "#e0e0e0",
    },
  },

  processingOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(255, 255, 255, 0.85)",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    zIndex: 2,
    padding: "8px",
    boxSizing: "border-box",
  },
  listBlocker: {
    position: "relative",
  },
  listOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(255, 255, 255, 0.6)",
    zIndex: 1,
    borderRadius: "4px",
  },
  fileListHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: "8px",
  },
  sizeCounter: {
    fontSize: "12px",
    color: "#605e5c",
  },
  fileList: {
    marginTop: "0",
    listStyleType: "none",
    padding: 0,
    maxHeight: "135px",
    overflowY: "auto",
    marginBottom: "4px",
  },
  fileListItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "2px 8px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
    marginBottom: "4px",
    gap: "8px",
  },
  fileInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "0",
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    fontSize: "11px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  fileSize: {
    fontSize: "12px",
    color: "#605e5c",
  },
  errorText: {
    color: "#d13438",
    fontSize: "12px",
  },
  thickProgressBar: {
    height: "18px !important",
    borderRadius: "9px !important",
    "& div": {
      height: "18px !important",
      borderRadius: "9px !important",
    }
  },

});

/**
 * UploadModal Component
 * 
 * A modal dialog for uploading files. It handles file selection, deep validation (count,
 * individual size, total size, duplicates, encryption checking, and file type), and displays errors.
 * It fetches its configuration limits from a backend API upon opening.
 * 
 * @param {Object} props - The component properties.
 * @param {boolean} props.isOpen - Boolean to control modal visibility.
 * @param {Function} props.onClose - Callback to close the modal.
 * @param {Function} props.onUpload - Callback to trigger the upload of valid, selected files.
 * @param {boolean} props.isUploading - True if files are actively being uploaded to the server.
 * @param {string} props.uploadError - Error message string passed down from the parent.
 * @param {Function} props.onClearUploadError - Callback to clear the active upload error.
 * @param {Function} props.onBackgroundCleanup - Callback to trigger a silent API deletion of a file.
 * @param {number} props.existingFileCount - Total number of files the user already has uploaded.
 * @param {number} props.existingFilesSize - Cumulative byte size of files the user has uploaded.
 * @param {string[]} props.existingFileNames - Array of previously uploaded file names.
 * @param {Object} props.uploadQuotaData - Data defining token usage limitations.
 * @param {Function} props.onClearQuotaData - Callback to clear active quota limitation state.
 * @param {number} props.maxTokenLimit - Absolute token limit threshold for the user.
 * @param {number} props.totalToken - The current number of tokens utilized by the user.
 * @param {Array} props.existingFiles - Array of objects representing the files already on the server.
 * @param {Function} props.onDeleteExistingFile - Callback to trigger an explicit deletion of an existing file.
 * @returns {JSX.Element} The rendered UploadModal component.
 */
const UploadModal = ({ isOpen, onClose, onUpload, isUploading, uploadError, onClearUploadError, onBackgroundCleanup, existingFileCount, existingFilesSize, existingFileNames, uploadQuotaData, onClearQuotaData, maxTokenLimit, totalToken, existingFiles, onDeleteExistingFile }) => {
  const styles = useStyles();
  const [config, setConfig] = useState({
    maxFiles: 10,
    maxFileSizeMb: 50,
  });
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false); // UI Loader
  const [progressText, setProgressText] = useState("");
  const [validationError, setValidationError] = useState("");
  const fileInputRef = React.useRef(null);
  const [deletingFileName, setDeletingFileName] = useState(null);

  /**
* Initiates the deletion of an already uploaded file from the server.
* Sets a local loading state for the specific file being deleted to show a progress spinner.
* 
* @param {string} fileName - The name of the file to be deleted.
*/
  const handleDeleteServerFile = async (fileName) => {
    setDeletingFileName(fileName);
    await onDeleteExistingFile(fileName);
    setSelectedFiles(prev => prev.filter(f => f.name !== fileName));
    setDeletingFileName(null);
  };

  /**
  * Effect hook to fetch upload configuration from the backend API when the modal opens.
  * This allows for dynamic control over upload limits without code changes.
  */
  useEffect(() => {
    // Only fetch when the modal is opened
    if (isOpen) {
      setIsLoadingConfig(true);
      fetch('/api/config')
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(data => {
          // Update state with fetched values
          setConfig({
            maxFiles: parseInt(data.maxFiles, 10),
            maxFileSizeMb: parseInt(data.maxFileSizeMb, 10),
          });
        })
        .catch(error => {
          console.error("Failed to fetch upload configuration. Using default values.", error);
        })
        .finally(() => {
          setIsLoadingConfig(false);
        });
    }
  }, [isOpen]);


  // Deriving constants from state for use in validation logic.
  const MAX_FILES = config.maxFiles;
  const MAX_FILE_SIZE_MB = config.maxFileSizeMb;
  const MAX_TOTAL_SIZE_MB = MAX_FILES * MAX_FILE_SIZE_MB;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

  /**
* Memoized calculation of the remaining file slots the user has available.
*/
  const availableSlots = useMemo(() => {
    return Math.max(0, MAX_FILES - existingFileCount);
  }, [MAX_FILES, existingFileCount]);

  /**
 * Memoized calculation of the remaining total byte size available for new uploads.
 */
  const availableSizeInBytes = useMemo(() => {
    return Math.max(0, MAX_TOTAL_SIZE_BYTES - existingFilesSize);
  }, [MAX_TOTAL_SIZE_BYTES, existingFilesSize]);

  /**
 * Memoized calculation of the combined size of all currently staged/selected files.
 */
  const totalSize = useMemo(() =>
    selectedFiles.reduce((sum, file) => sum + file.size, 0),
    [selectedFiles]
  );

  /**
* Memoized calculation of the current token usage.
* Scans both existing files on the server and recently added valid files in the modal
* to determine if the collective footprint exceeds the API token limits.
*/
  const currentTokenCount = useMemo(() => {
    if (!uploadQuotaData) return null;
    let sum = 0;
    const countedFiles = new Set();
    // Add tokens of files already on the server
    if (existingFileNames) {
      existingFileNames.forEach(name => {
        if (!countedFiles.has(name)) {
          sum += uploadQuotaData.fileTokens[name] || 0;
          countedFiles.add(name);
        }
      });
    }
    // Add tokens of newly selected files in the modal
    if (selectedFiles) {
      selectedFiles.forEach(file => {
        if (!countedFiles.has(file.name)) {
          sum += uploadQuotaData.fileTokens[file.name] || 0;
          countedFiles.add(file.name);
        }
      });
    }
    return sum;
  }, [selectedFiles, existingFileNames, uploadQuotaData]);

  // True if the real-time count is higher than the max limit
  const isTokenLimitExceeded = uploadQuotaData && currentTokenCount > uploadQuotaData.maxLimit;

  /**
 * Effect hook to reset the modal's internal visual state every time it is opened.
 */
  useEffect(() => {
    if (isOpen) {
      setSelectedFiles([]);
      setValidationError("");
      setProgressText("");
    }
  }, [isOpen]);

  /**
  * Effect hook to synchronize the modal's state when the parent signals a quota error.
  * When `uploadQuotaData` is set, it means the files previously in `selectedFiles`
  * have been processed by the backend. This clears the modal's stage.
  */
  useEffect(() => {
    if (uploadQuotaData) {
      setSelectedFiles([]);
    }
  }, [uploadQuotaData]);


  /**
     * The core validation engine effect. 
     * Runs whenever the list of selected files changes and verifies it against global size 
     * and slot limits, updating the UI validation error accordingly.
     */
  useEffect(() => {
    if (isUploading) {
      setValidationError("");
      return;
    }
    if (selectedFiles.length > availableSlots) {
      const excessFiles = selectedFiles.length - availableSlots;

      let message;
      if (availableSlots === MAX_FILES) {
        // User has 0 files uploaded, and selected 51 (Limit 50)
        message = `You selected ${selectedFiles.length} files, which exceeds the maximum limit of ${MAX_FILES}. Please remove ${excessFiles} file(s).`;
      } else {
        // User has 40 files, limit is 50 (10 left), and selected 15.
        message = `You selected ${selectedFiles.length} files, but you only have space for ${availableSlots} more. Please remove ${excessFiles} file(s).`;
      }

      setValidationError(message);
      return;
    }
    const oversizedFiles = selectedFiles.filter(file => file.size > MAX_FILE_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
      const oversizedFileNames = oversizedFiles.map(f => f.name).join(', ');
      setValidationError(
        `File(s) too large. The following exceed the ${MAX_FILE_SIZE_MB}MB limit: ${oversizedFileNames}.`
      );
      return;
    }
    if (totalSize > availableSizeInBytes) {
      const currentSizeMB = (totalSize / 1024 / 1024).toFixed(1);
      const availableSizeMB = (availableSizeInBytes / 1024 / 1024).toFixed(1);
      setValidationError(
        `Total size is too large. Your selection is ${currentSizeMB}MB, but you only have ${availableSizeMB}MB remaining. Please remove files.`
      );
      return;
    }
    setValidationError("");
  }, [selectedFiles, totalSize, availableSlots, availableSizeInBytes, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB]);

  /**
  * Evaluates and processes a single file, checking for duplicates against both
  * the modal's local state and the server's existing files.
  * 
  * @param {File} file - The file object to evaluate.
  * @returns {Promise<Object>} An object containing the status ('resolved' or 'rejected') and reason.
  */
  // eslint-disable-next-line no-unused-vars    
  const processSingleFile = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    // Check for duplicates
    if (selectedFiles.some(f => f.name === file.name) || (existingFileNames && existingFileNames.includes(file.name))) {
      return { status: 'rejected', reason: `Duplicate: ${file.name}` };
    }

    // Process based on type
    try {
      if (ext === 'pdf') {
        return { status: 'resolved', file: file };
      }
      else if (ext === 'doc' || ext === 'docx') {
        const convertedPdf = await convertDocToPdf(file);
        return { status: 'resolved', file: convertedPdf };
      }
      else {
        return { status: 'rejected', reason: `Invalid Type: ${file.name}` };
      }
    } catch (error) {
      return { status: 'rejected', reason: `Error (${file.name}): ${error.message}` };
    }
  };

  /**
 * Performs deep validation on a PDF file by utilizing PDF.js to parse the document.
 * Detects internal corruptions, PasswordException (encryption), and excessive page counts.
 * 
 * @param {File} file - The PDF file object to inspect.
 * @returns {Promise<File>} The validated file object.
 * @throws {Error} Throws specific error reasons if the PDF is unusable.
 */
  const validatePdf = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      if (arrayBuffer.byteLength === 0) throw new Error(`Corrupted: ${file.name} is empty.`);

      let pdfDoc;
      try {
        // It will automatically try a blank password for restricted files.
        const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) });
        pdfDoc = await loadingTask.promise;
      } catch (err) {
        // Log the error so we can see if Webpack/Vite is blocking the worker
        console.error("PDF Parsing Error for", file.name, ":", err);

        // If it throws PasswordException, it actually requires a typed password to open
        if (err.name === 'PasswordException') {
          throw new Error(`Encrypted: ${file.name} is password-protected.`);
        }
        // If pdfjsLib isn't imported right, tell the developer
        if (err.name === 'ReferenceError' || err.message?.includes('pdfjsLib')) {
          throw new Error(`Configuration Error: PDF.js is not imported correctly.`);
        }
        // Otherwise, the file structure is genuinely broken
        throw new Error(`Corrupted: ${file.name} is not a valid PDF.`);
      }
      // .numPages instead of getPageCount()
      const pages = pdfDoc.numPages;
        
      // Check if the PDF has any readable text across its pages
      let hasText = false;
      for (let i = 1; i <= pages; i++) {
        const page = await pdfDoc.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item) => item.str).join("").trim();
        if (pageText.length > 0) {
          hasText = true;
          break; // Found text, stop checking
        }
      }

      if (!hasText) {
        throw new Error(`Rejected: ${file.name} is a blank document.`);
      }  
        
      if (pages > 1000) {
        throw new Error(`Rejected: ${file.name} has too many pages (${pages}). Max allowed is 1000.`);
      }
      return file;
    } catch (error) {
      if (
        error.message.includes("Encrypted") ||
        error.message.includes("Rejected") ||
        error.message.includes("Configuration")
      ) {
        throw error;
      }
      throw new Error(`Corrupted: ${file.name} is not a valid PDF.`);
    }
  };

  /**
 * Performs deep validation on Word documents (.doc, .docx).
 * Uses JSZip to peek into the XML metadata of DOCX files to determine page limits
 * and checks hex signatures to detect OLE encryption (Password-to-Open).
 * 
 * @param {File} file - The document file to evaluate.
 * @returns {Promise<File>} The successfully validated file object.
 * @throws {Error} Throws specific error reasons if the DOC/DOCX is unusable.
 */
  const validateDocFile = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      if (arrayBuffer.byteLength === 0) throw new Error(`Corrupted: ${file.name} is empty.`);

      const ext = file.name.split(".").pop().toLowerCase();
      const view = new Uint8Array(arrayBuffer);

      // Magic numbers for OLE (Encrypted DOCX or Standard Legacy DOC)
      // Hex: D0 CF 11 E0
      const isOLE = view[0] === 0xD0 && view[1] === 0xCF && view[2] === 0x11 && view[3] === 0xE0;

      if (ext === "docx") {
        // Standard DOCX files are ZIP files. 
        // If a DOCX is OLE, it means Microsoft Word applied a Password-to-Open encryption.
        if (isOLE) {
          throw new Error(`Encrypted: ${file.name} is password-protected.`);
        }

        try {

          if (typeof JSZip === 'undefined') {
            throw new Error(`Configuration Error: JSZip is not installed or imported.`);
          }

          const zip = new JSZip();
          await zip.loadAsync(arrayBuffer);
            
          // Check if word/document.xml contains any text
          const docXmlFile = zip.file("word/document.xml");
          let docText = "";
          if (docXmlFile) {
            const xmlContent = await docXmlFile.async("string");
            // Strips all XML tags to see if any user-written text remains
            docText = xmlContent.replace(/<[^>]+>/g, "").trim();
          }

          if (!docText) {
            throw new Error(`Rejected: ${file.name} is a blank document.`);
          }
            
          // Calculate pages from DOCX XML metadata
          const appXmlFile = zip.file("docProps/app.xml");
          let pages = 0;
          if (appXmlFile) {
            const appXmlText = await appXmlFile.async("string");
            const match = appXmlText.match(/<Pages>(\d+)<\/Pages>/i);
            if (match && match[1]) pages = parseInt(match[1], 10);
          }
          if (pages > 1000) {
            throw new Error(`Rejected: ${file.name} has too many pages (${pages}). Max allowed is 1000.`);
          }
          return file; // Read-Only/Restricted DOCX files will successfully return here
        } catch (zipError) {
          console.error(`DOCX Parsing Error for ${file.name}:`, zipError);

          if (zipError.message.includes("Rejected") || zipError.message.includes("Configuration")) {
            throw zipError;
          }
          throw new Error(`Corrupted: ${file.name} is a broken or invalid DOCX file.`);
        }
      }
        
         if (ext === "doc") {
            if (!isOLE) {
             throw new Error(`Corrupted: ${file.name} is not a valid DOC file.`);
            }

            // Locate the WordDocument stream header via its magic signature (0xEC, 0xA5) on a 512-byte sector boundary
            let ccpText = -1;
            for (let offset = 512; offset < view.length - 128; offset += 512) {
             if (view[offset] === 0xEC && view[offset + 1] === 0xA5) {
              const dataView = new DataView(arrayBuffer, offset);
              // Offset 0x4C (76) in the FIB is ccpText (character count of main body text, including 1 trailing CR)
              ccpText = dataView.getInt32(0x4C, true);
              break;
             }
            }

            // In a blank .doc file, ccpText is 0 or 1 (just the single end-of-document paragraph mark \r)
            if (ccpText !== -1 && ccpText <= 1) {
             throw new Error(`Rejected: ${file.name} is a blank document.`);
            }
            return file;
           }
        
      return file;
    } catch (error) {
      // Re-throw exact messages so the UI toaster is accurate.
      if (
        error.message.includes("Corrupted") ||
        error.message.includes("Encrypted") ||
        error.message.includes("Rejected") ||
        error.message.includes("Configuration")
      ) {
        throw error;
      }
      throw new Error(`Corrupted: ${file.name} could not be processed.`);
    }
  };

  /**
* Master handler for the native file input's `onChange` event.
* Evaluates the dropped/selected files, filters out duplicates and unsupported types,
* and sequentially runs the heavy asynchronous content validation pipeline.
* 
* @param {React.ChangeEvent<HTMLInputElement>} event - The file input change event.
*/
  const handleFileChange = async (event) => {
    if (onClearUploadError) onClearUploadError();
    if (onClearQuotaData) onClearQuotaData();
    const newlySelected = Array.from(event.target.files);
    if (newlySelected.length === 0) return;

    const allowedExtensions = ["pdf", "doc", "docx"];

    const filesToProcess = [];
    const duplicateInModal = [];
    const duplicateOnServer = [];
    const invalidTypeFiles = [];

    const alreadySelectedNames = new Set(selectedFiles.map((f) => f.name));
    const alreadyUploadedNames = new Set(existingFileNames || []); // Fallback to empty array just in case

    // Initial Synchronous Validation (Duplicates and Types)
    newlySelected.forEach((newFile) => {
      const fileName = newFile.name || "";
      const fileExtension = fileName.slice(fileName.lastIndexOf(".") + 1).toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
        invalidTypeFiles.push(fileName);
      } else if (alreadySelectedNames.has(newFile.name)) {
        duplicateInModal.push(newFile.name);
      } else if (alreadyUploadedNames.has(newFile.name)) {
        duplicateOnServer.push(newFile.name);
      } else {
        filesToProcess.push(newFile);
      }
    });

    // Show toasts for instantly rejected files
    if (invalidTypeFiles.length > 0) {
      toast.error(`Invalid file type(s): ${invalidTypeFiles.join(", ")}. Please select PDF, DOC, or DOCX files.`, { duration: 6000 });
    }
    if (duplicateInModal.length > 0) {
      toast.error(`Already selected: ${duplicateInModal.join(", ")}`, { duration: 6000 });
    }
    if (duplicateOnServer.length > 0) {
      toast.error(`Already uploaded: ${duplicateOnServer.join(", ")}`, { duration: 6000 });
    }

    if (filesToProcess.length === 0) {
      event.target.value = null;
      return;
    }

    // Start Deep Asynchronous Validation (Encryption & Corruption)
    setIsProcessing(true);
    setProgressText(`Queuing ${filesToProcess.length} file(s) for validation...`);

    const successfulFiles = [];
    const failedMessages = [];

    try {
      let completed = 0;

      // Validate files sequentially to keep memory stable
      for (const file of filesToProcess) {
        const ext = file.name.split(".").pop().toLowerCase();

        try {
          let validatedFile;

          if (ext === "pdf") {
            setProgressText(`Validating ${file.name}... (${completed + 1}/${filesToProcess.length})`);
            validatedFile = await validatePdf(file);
          } else if (ext === "doc" || ext === "docx") {
            setProgressText(`Validating ${file.name}... (${completed + 1}/${filesToProcess.length})`);
            validatedFile = await validateDocFile(file);

            // Allow WASM memory to clear out between Word documents
            //setProgressText(`Clearing memory...`);
            //await new Promise((resolve) => setTimeout(resolve, 300));
          }

          // If no error was thrown, it's valid. Add to successful array.
          successfulFiles.push(validatedFile);

        } catch (err) {
          // If validatePdf or validateDocFile throws an error, catch it here
          failedMessages.push(err?.message || `Failed to read ${file.name}`);
        }

        completed += 1;
        setProgressText(`Validated ${completed} of ${filesToProcess.length} file(s)`);
      }

      // Display specific errors (Corrupted or Encrypted) to the user
      if (failedMessages.length > 0) {
        failedMessages.forEach((msg) => {
          toast.error(msg, { duration: 8000 });
        });
      }

      // Add all successfully validated files to the modal UI
      if (successfulFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...successfulFiles]);
        setProgressText(`Done. Added ${successfulFiles.length} file(s).`);
      } else {
        setProgressText("");
      }

    } catch (error) {
      console.error("Critical error during file validation:", error);
      toast.error("An unexpected error occurred while validating the files.");
    } finally {
      setIsProcessing(false);
      event.target.value = null; // Reset input so user can click and select files again
    }
  };

  /**
* Removes a specific file from the `selectedFiles` staging list.
* Checks if the file token data indicates it was known to the backend; if so,
* invokes an API call to quietly clean up the server resources.
* 
* @param {string} fileNameToRemove - The exact filename to purge.
*/
  const handleRemoveFile = (fileNameToRemove) => {
    //  Remove it from the UI immediately
    setSelectedFiles(prevFiles =>
      prevFiles.filter(file => file.name !== fileNameToRemove)
    );
    if (onClearUploadError) onClearUploadError();
    const isKnownToBackend = uploadQuotaData
      && uploadQuotaData.fileTokens
      && uploadQuotaData.fileTokens[fileNameToRemove] !== undefined;

    // ONLY call the delete API if the backend has the file
    if (isKnownToBackend && onBackgroundCleanup) {
      onBackgroundCleanup(fileNameToRemove);
    } else {
      console.log(`[Cleanup] ${fileNameToRemove} was only local. No API call needed.`);
    }
  };

  /**
 * Triggers the hidden native file input element when the large, stylized
 * drag-and-drop / select area is clicked. Blocks the click if disabled.
 */
  const handleContainerClick = () => {
    if (isUploading || isProcessing || isTokenLimitExceeded) return;
    fileInputRef.current.click();
  };

  /**
 * Completes the UI flow by invoking the parent's `onUpload` handler, 
 * passing up the finalized array of fully validated files.
 */
  const handleUploadClick = () => {
    if (selectedFiles.length > 0 && !validationError) {
      onUpload(selectedFiles);
    }
  };

  /**
 * Manages the intent to close the entire upload modal.
 * Actively prevents closure if the token limit is currently exceeded to force user resolution.
 */
  const handleClose = () => {
    if (isTokenLimitExceeded) {
      toast.error("Quota exceeded! Please remove extra files.", { id: "upload-modal-lock" });
      return;
    }
    onClose();
  }

  //  For Dynamic Progress Bar Color 
  const tokenUsageRatio = (maxTokenLimit && maxTokenLimit > 0) ? (totalToken / maxTokenLimit) : 0;
  const tokenPercentage = Math.min(tokenUsageRatio * 100, 100).toFixed(1);

  let progressColor = "success";
  let tokenTextColor = "#107c10";
  if (tokenUsageRatio >= 0.9) {
    progressColor = "error";
    tokenTextColor = "#d13438";
  } else if (tokenUsageRatio >= 0.7) {
    // eslint-disable-next-line no-unused-vars    
    progressColor = "warning";
    // eslint-disable-next-line no-unused-vars    
    tokenTextColor = "#d83b01";
  }

  return (
    <Dialog open={isOpen} onOpenChange={(event, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          <DialogTitle className={styles.dialogTitle}>Upload Documents</DialogTitle>
          {isLoadingConfig ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <Spinner label="Loading upload limits..." />
            </div>
          ) : (
            <>
              <div className={styles.fileInputContainer} onClick={handleContainerClick} aria-disabled={isUploading || isProcessing || isTokenLimitExceeded}>
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  accept=".pdf,.doc,.docx"
                  disabled={isUploading || isProcessing || isTokenLimitExceeded}
                />
                <Text style={{ fontSize: "14px", fontWeight: "500", marginBottom: "12px", color: "#323130" }}>
                  Click to upload files.
                </Text>
                <Text style={{ fontSize: "11px", color: "#605e5c"}}>
                    ( Files format: PDF, DOC, DOCX only )
                </Text>
                {/* MODERN PROGRESS DESIGN */}
                {totalToken > 0 && maxTokenLimit ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>

                    {/* File Count Pill */}
                    <div style={{
                      backgroundColor: "#f3f2f1",
                      padding: "4px 12px",
                      borderRadius: "16px",
                      fontSize: "11px",
                      color: "#605e5c",
                      fontWeight: "500",
                      marginBottom: "16px"
                    }}>
                      📁 {existingFileCount} of {MAX_FILES} Files Uploaded
                    </div>

                    {/* Token Storage Meter */}
                    <div style={{ width: '90%', maxWidth: '350px', paddingBottom: '1px' }} className={styles.thickProgressBox}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '8px' }}>
                        <span style={{ color: "#605e5c", fontWeight: "600" }}>Token Limit:</span>

                        {/* NOW SHOWING THE ACTUAL NUMBERS HERE */}
                        <span style={{ color: "#323130", fontWeight: "600" }}>
                          {totalToken.toLocaleString()} / {maxTokenLimit.toLocaleString()} Tokens Used
                        </span>
                      </div>

                      {/* WRAPPED IN A TOOLTIP TO SHOW PERCENTAGE ON HOVER */}
                      {/*<Tooltip content={`${tokenPercentage}% Used`} relationship="label" withArrow> */}
                      <div style={{ position: "relative", margin: "8px 0px", display: "flex", alignItems: "center", marginBottom: "0px" }}>
                        <ProgressBar
                          className={styles.thickProgressBar}
                          value={Math.min(tokenUsageRatio, 1)}
                          color="brand"
                        // thickness="large"
                        />
                        {/* Absolutely positioned text in the dead center */}
                        <span style={{
                          position: "absolute",
                          width: "100%",
                          textAlign: "center",
                          fontSize: "11px",
                          fontWeight: "bold",
                          color: tokenUsageRatio >= 0.5 ? "#ffffff" : "#323130",
                          pointerEvents: "none",
                          textShadow: tokenUsageRatio < 0.5 ? "0px 0px 2px #ffffff" : "none"
                        }}>
                          {tokenPercentage}%
                        </span>
                      </div>
                      {/*</Tooltip> */}
                      {/* Warning message directly under the bar to save space */}
                      {isTokenLimitExceeded && (
                        <Text style={{ color: "#d13438", fontSize: "11px", fontWeight: "500", display: "block", marginTop: "8px" }}>
                          ⚠️ Quota exceeded. Please remove files from the list below.
                        </Text>
                      )}
                    </div>

                  </div>
                ) : (
                  <Text size="small" style={{ color: "#605e5c", fontSize: "12px", marginTop: "5px" }}>
                    {existingFileCount > 0
                      ? `${existingFileCount} / ${MAX_FILES} Files Uploaded`
                      : `Max ${MAX_FILES} Files`}
                    {" | "}
                    {maxTokenLimit ? `Max ${maxTokenLimit.toLocaleString()} Tokens` : "Loading limits..."}
                  </Text>
                )}

                {isProcessing && (
                  <div className={styles.processingOverlay} role="status" aria-live="polite">
                    <Spinner size="small" label="Processing..." />
                    {/*<Text style={{ fontSize: "11px", fontWeight: "500", marginTop: "2px" }}>{progressText || "Preparing files..."}</Text>  */}
                    <Text
                      title={progressText}
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        marginTop: "2px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                        display: "block",
                        textAlign: "center"
                      }}
                    >
                      {progressText || "Preparing files..."}
                    </Text>
                    <Text style={{ fontSize: "10px", color: "#605e5c" }}>
                      Please keep this dialog open.
                    </Text>
                  </div>
                )}

              </div>

              {!uploadQuotaData && selectedFiles.length > 0 && (
                <div className={styles.listBlocker}>

                  {/* the list while processing */}
                  {isProcessing && <div className={styles.listOverlay} />}

                  <div className={styles.fileListHeader}>
                    <Text weight="semibold" style={{ fontSize: "12px" }}> Selected Files ({selectedFiles.length} / {availableSlots})</Text>
                  </div>
                  <ul className={styles.fileList}>
                    {selectedFiles.map((file) => (
                      <li key={file.name + file.lastModified} className={styles.fileListItem}>
                        <div className={styles.fileInfo}>
                          <Tooltip content={file.name} relationship="label">
                            {/* Make oversized file names red */}
                            <Text
                              className={styles.fileName}
                              style={{ color: file.size > MAX_FILE_SIZE_BYTES ? '#d13438' : undefined }}
                            >
                              {file.name}
                            </Text>
                          </Tooltip>
                          <Text className={styles.fileSize}>
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </Text>
                        </div>
                        <Tooltip content="Remove file" relationship="label">
                          <Button
                            appearance="transparent"
                            size="small"
                            icon={<Dismiss24Regular />}
                            onClick={() => handleRemoveFile(file.name)}
                            disabled={isUploading || isProcessing}
                          />
                        </Tooltip>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* SHOW EXISTING FILES TO DELETE IF QUOTA EXCEEDED */}
              {uploadQuotaData && isTokenLimitExceeded && existingFiles && existingFiles.length > 0 && (
                <div className={styles.listBlocker}>
                  <div className={styles.fileListHeader}>
                    <Text weight="semibold" style={{ color: "#d13438", fontSize: "12px" }}>
                      Previously Uploaded Files (Delete to free up space)
                    </Text>
                  </div>
                  <ul className={styles.fileList} style={{ maxHeight: "150px", border: "1px solid #fde7e9", borderRadius: "4px", padding: "4px", marginBottom: "0px" }}>
                    {existingFiles.map((file) => (
                      <li key={`existing-${file.name}`} className={styles.fileListItem} style={{ backgroundColor: "#fff" }}>
                        <div className={styles.fileInfo}>
                          <Tooltip content={file.name} relationship="label">
                            <Text className={styles.fileName}>{file.name}</Text>
                          </Tooltip>
                          <Text className={styles.fileSize}>
                            ({file.tokens != null ? file.tokens.toLocaleString() : "Unknown"} Tokens)
                          </Text>
                        </div>
                        <Tooltip content="Delete this file from server" relationship="label">
                          <Button
                            appearance="transparent"
                            size="small"
                            icon={deletingFileName === file.name ? <Spinner size="tiny" /> : <Dismiss24Regular />}
                            onClick={() => handleDeleteServerFile(file.name)}
                            disabled={isUploading || isProcessing || deletingFileName !== null}
                          />
                        </Tooltip>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Display standard validation errors (but hide the raw QUOTA_EXCEEDED keyword) */}
              {(validationError || (uploadError && uploadError !== "QUOTA_EXCEEDED")) && (
                <Text className={styles.errorText}>{validationError || uploadError}</Text>
              )}

              <DialogActions className={styles.dialogActions}>
                {/* If we are in quota mode AND the limit is exceeded, show nothing (or a disabled close) */}
                {uploadQuotaData && isTokenLimitExceeded ? (
                  <>
                    <div style={{ flexGrow: 1 }} />
                    <Button appearance="secondary" disabled>Cancel</Button>
                    <Button appearance="primary" disabled>Upload</Button>
                  </>
                ) : uploadQuotaData && !isTokenLimitExceeded ? (
                  // In quota mode AND resolved, show a single "Done" button
                  <>
                    <div style={{ flexGrow: 1 }} />
                    <Button style={{ fontSize: "12px" }} appearance="primary" onClick={handleClose}>
                      Done
                    </Button>
                  </>
                ) : (
                  // Original buttons for the normal upload flow
                  <>
                    <Button style={{ fontSize: "12px", width: "100%" }} appearance="secondary" onClick={handleClose} disabled={isUploading || isProcessing}>
                      Cancel
                    </Button>
                    <Button
                      style={{ fontSize: "12px", width: "100%" }}
                      appearance="primary"
                      onClick={handleUploadClick}
                      // Prevent upload if token limit is dynamically exceeded
                      disabled={isUploading || isProcessing || selectedFiles.length === 0 || !!validationError || isTokenLimitExceeded}
                    >
                      {isUploading ? <Spinner size="tiny" /> : "Upload"}
                    </Button>
                  </>
                )}
              </DialogActions>
            </>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

UploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  isUploading: PropTypes.bool.isRequired,
  uploadError: PropTypes.string,
  onClearUploadError: PropTypes.func,
  onBackgroundCleanup: PropTypes.func,
  existingFileCount: PropTypes.number.isRequired,
  existingFilesSize: PropTypes.number.isRequired,
  existingFileNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  uploadQuotaData: PropTypes.object,
  onClearQuotaData: PropTypes.func,
  maxTokenLimit: PropTypes.number,
  totalToken: PropTypes.number,
  existingFiles: PropTypes.array,
  onDeleteExistingFile: PropTypes.func
};

export default UploadModal;